import { Dispatch, SetStateAction, useMemo } from 'react';
import { CandleChart as LegacyCandleChart } from '../../../../../ProfitCalculations/Content/SingleProfitCalculation/Result/CandleChart';
import { XAxisInfo } from '../../../../../../components/MultiChart';
import { useResultData } from '../../context';
import {
  windowForLiveMrcDeepDive,
  windowForLiveState,
  windowForMrcDeepDive,
  windowForTransaction,
} from '../../derived/candles';
import { computeCbrtLines, computeMrcLines } from '../../derived/overlays';
import {
  CompactTransaction,
  MrcEpisode,
} from '../../../../../ProfitCalculations/types';

interface Props {
  txId?: number;
  live?: boolean;
  /** When set, the chart zooms to this MRC episode and draws its phase lines. */
  mrcEpisode?: MrcEpisode;
  /** Injected by MultiChart. */
  width?: number;
  height?: number;
  setXAxisInfo?: Dispatch<SetStateAction<XAxisInfo | undefined>>;
  xAxisInfo?: XAxisInfo;
}

const EMPTY_WINDOW = { candles: [], start: 0, end: 0, transactions: [] };

const CandleChart = ({
  txId,
  live,
  mrcEpisode,
  width,
  height,
  setXAxisInfo,
  xAxisInfo,
}: Props) => {
  const { raw } = useResultData();

  const candleWindow = useMemo(() => {
    if (mrcEpisode && txId !== undefined) {
      return windowForMrcDeepDive(txId, mrcEpisode, raw);
    }
    if (mrcEpisode && live) {
      return windowForLiveMrcDeepDive(mrcEpisode, raw);
    }
    if (live) return windowForLiveState(raw);
    if (txId !== undefined) return windowForTransaction(txId, raw);
    return EMPTY_WINDOW;
  }, [raw, txId, live, mrcEpisode]);

  const overlayLines = useMemo(() => {
    if (candleWindow.candles.length === 0) {
      return [];
    }
    if (mrcEpisode) {
      // Deep dive — only the focused episode's phase lines.
      return computeMrcLines([mrcEpisode], candleWindow);
    }
    // Standard expanded view — cbrt range lines + every MRC episode line.
    return [
      ...computeCbrtLines(raw.cbrtRangeStates, candleWindow),
      ...computeMrcLines(raw.mrcStates, candleWindow),
    ];
  }, [candleWindow, mrcEpisode, raw]);

  if (!width || !height || candleWindow.candles.length === 0) {
    return null;
  }

  return (
    <LegacyCandleChart
      candles={candleWindow.candles}
      compactTransactions={candleWindow.transactions as CompactTransaction[]}
      signals={[]}
      rootDimensions={{ width, height }}
      setXAxisInfo={setXAxisInfo}
      xAxisInfo={xAxisInfo}
      sharedXAxis
      niceYTicks
      overlayLines={overlayLines}
    />
  );
};

export default CandleChart;
